/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */
import { v4 as uuidv4 } from 'uuid'
import { offlineStorageService } from './offlineStorage'
import { supabase } from '../../../services/supabase/client'
import { withTimeout, SYNC_OP_TIMEOUT_MS } from './withTimeout'
import { isValidStorageKey, sanitizeStorageFileName } from './storageKey'
import {
  purgeJobStorage,
  purgeNodeStorage,
  purgePhotoStorage,
} from '../../../services/storage/storageCleanup'

export type SyncOperation = 'create' | 'update' | 'delete'
export type SyncEntity = 'job' | 'node' | 'photo' | 'report'
export type SyncStatus = 'pending' | 'in_progress' | 'completed' | 'failed'

/** Automatic retries before an item is dead-lettered (status 'failed') */
export const MAX_SYNC_RETRIES = 3

/**
 * An 'in_progress' item older than this is treated as stranded by an
 * interrupted run (tab killed/suspended mid-sync) and reclaimed to
 * 'pending'. Generous: one item can chain several ops, each bounded by
 * SYNC_OP_TIMEOUT_MS.
 */
export const STALE_IN_PROGRESS_MS = 5 * 60_000

export interface SyncQueueItem {
  id: string
  operation: SyncOperation
  entity: SyncEntity
  entityId: string
  data: any
  timestamp: number
  status: SyncStatus
  retryCount: number
  error?: string
  /** When the current/last processing attempt started (set with 'in_progress') */
  startedAt?: number
}

/**
 * Build a queue item for an offline mutation. Exported so services can pass
 * it to the atomic entity+queue writes on offlineStorageService (REL-09).
 */
export const buildSyncQueueItem = (
  operation: SyncOperation,
  entity: SyncEntity,
  entityId: string,
  data: any
): SyncQueueItem => ({
  id: uuidv4(),
  operation,
  entity,
  entityId,
  data,
  timestamp: Date.now(),
  status: 'pending',
  retryCount: 0,
})

/**
 * Supabase Storage reports an existing object as a 409/"already exists"
 * error; shape varies across client versions, so match both fields.
 */
const isDuplicateUploadError = (error: any): boolean =>
  error?.statusCode === '409' ||
  error?.statusCode === 409 ||
  /already exists|duplicate/i.test(String(error?.message ?? ''))

/**
 * A 23505 can mean two things on inspection_nodes: our client-generated id
 * already exists (a replay of an insert whose response was lost — success),
 * or a concurrent client claimed our node number (retry with a new one).
 */
const isPrimaryKeyConflict = (error: any): boolean =>
  /pkey/i.test(String(error?.message ?? '') + String(error?.details ?? ''))

/**
 * Sync queue service
 * - Queues operations when offline
 * - Processes queue when online
 * - Handles retry logic for failed syncs
 * - Maintains data consistency
 */
export const syncQueueService = {
  /**
   * Add an operation to the sync queue
   */
  async addToQueue(
    operation: SyncOperation,
    entity: SyncEntity,
    entityId: string,
    data: any
  ): Promise<void> {
    await offlineStorageService.putQueueItem(buildSyncQueueItem(operation, entity, entityId, data))
  },

  /**
   * Get items eligible for automatic sync. Dead-lettered items ('failed')
   * are excluded — they only move via manual retry/discard.
   */
  async getPendingItems(): Promise<SyncQueueItem[]> {
    const allItems = await offlineStorageService.getQueueItems()
    return allItems
      .filter((item) => item.status === 'pending')
      .sort((a, b) => a.timestamp - b.timestamp)
  },

  /**
   * Return items stranded 'in_progress' by an interrupted run to 'pending'.
   * A run that dies before its catch block writes the item back (tab
   * killed, iOS suspending the page mid-upload) leaves it 'in_progress'
   * forever — invisible to getPendingItems AND to the dead-letter panel.
   * Every sync op is replay-safe (409-tolerated uploads, replay checks,
   * PK-conflict refetch), so reclaiming after a grace period is safe even
   * if another tab is genuinely mid-flight.
   */
  async reclaimStaleInProgress(): Promise<number> {
    const allItems = await offlineStorageService.getQueueItems()
    const cutoff = Date.now() - STALE_IN_PROGRESS_MS
    let reclaimed = 0
    for (const item of allItems) {
      if (item.status !== 'in_progress') continue
      // Items written before startedAt existed reclaim immediately
      if (typeof item.startedAt === 'number' && item.startedAt > cutoff) continue
      item.status = 'pending'
      await offlineStorageService.putQueueItem(item)
      reclaimed++
    }
    return reclaimed
  },

  /**
   * Process the entire sync queue.
   * `deadLettered` counts items that exhausted their retries during this run.
   */
  async processQueue(): Promise<{ success: number; failed: number; deadLettered: number }> {
    await this.reclaimStaleInProgress()
    const pendingItems = await this.getPendingItems()
    let success = 0
    let failed = 0
    let deadLettered = 0

    for (const staleItem of pendingItems) {
      // Re-read the item: an earlier item in this run may have rewritten it
      // (a node create remaps temp ids on queued photos behind it)
      const item = (await offlineStorageService.getQueueItem(staleItem.id)) ?? staleItem
      if (item.status !== 'pending') continue

      try {
        await this.processSyncItem(item)
        success++
      } catch (error) {
        failed++
        // processSyncItem updated the item in place before rethrowing
        // (widen: TS still sees the 'pending' narrowing from the guard above)
        if ((item.status as SyncStatus) === 'failed') deadLettered++
        console.error(`Failed to sync item ${item.id}:`, error)
      }
    }

    return { success, failed, deadLettered }
  },

  /**
   * Process a single sync item
   */
  async processSyncItem(item: SyncQueueItem): Promise<void> {
    // Update status to in_progress; startedAt lets reclaimStaleInProgress
    // tell a live attempt from one stranded by an interrupted run
    item.status = 'in_progress'
    item.startedAt = Date.now()
    await offlineStorageService.putQueueItem(item)

    try {
      // Execute the sync operation based on entity type
      switch (item.entity) {
        case 'job':
          await this.syncJob(item)
          break
        case 'node':
          await this.syncNode(item)
          break
        case 'photo':
          await this.syncPhoto(item)
          break
        case 'report':
          await this.syncReport(item)
          break
        default:
          throw new Error(`Unknown entity type: ${String(item.entity)}`)
      }

      // Mark as completed and remove from queue
      await offlineStorageService.deleteQueueItem(item.id)
    } catch (error) {
      item.retryCount++
      item.error = error instanceof Error ? error.message : 'Unknown error'

      // Never delete captured data (REL-01). Within the retry budget the item
      // returns to 'pending' for the next automatic sync; beyond it, it is
      // dead-lettered as 'failed' and waits for manual retry/discard.
      item.status = item.retryCount >= MAX_SYNC_RETRIES ? 'failed' : 'pending'
      await offlineStorageService.putQueueItem(item)

      throw error
    }
  },

  /**
   * Sync job operations
   */
  async syncJob(item: SyncQueueItem): Promise<void> {
    const { operation, entityId, data } = item

    switch (operation) {
      case 'create': {
        const insertJob = () =>
          withTimeout(
            supabase.from('jobs').insert(data).select().single(),
            SYNC_OP_TIMEOUT_MS,
            'job insert'
          )

        let { data: serverJob, error: createError } = await insertJob()
        if (createError?.code === '23505' && data.id && isPrimaryKeyConflict(createError)) {
          // Replay: an earlier attempt created this job but its response was
          // lost — fetch the existing row instead of duplicating it
          ;({ data: serverJob, error: createError } = await withTimeout(
            supabase.from('jobs').select('*').eq('id', data.id).single(),
            SYNC_OP_TIMEOUT_MS,
            'job refetch'
          ))
        }
        if (createError) throw createError
        if (!serverJob) throw new Error('Job insert returned no row')

        if (entityId.startsWith('temp_')) {
          // Legacy offline job (pre client-generated ids): swap the server
          // id into local nodes/photos and queued items — including the
          // temp-prefixed storage keys queued photos would upload under
          await offlineStorageService.remapJobId(entityId, serverJob)
        } else {
          await offlineStorageService.saveJob(serverJob)
        }
        break
      }

      case 'update': {
        const { error: updateError } = await withTimeout(
          supabase.from('jobs').update(data).eq('id', entityId),
          SYNC_OP_TIMEOUT_MS,
          'job update'
        )
        if (updateError) throw updateError
        break
      }

      case 'delete': {
        // Storage before the row — the bucket policies resolve access
        // through the job, so afterwards the files are unreachable
        await purgeJobStorage(entityId)
        const { error: deleteError } = await withTimeout(
          supabase.from('jobs').delete().eq('id', entityId),
          SYNC_OP_TIMEOUT_MS,
          'job delete'
        )
        if (deleteError) throw deleteError
        break
      }
    }
  },

  /**
   * Sync node operations
   */
  async syncNode(item: SyncQueueItem): Promise<void> {
    const { operation, entityId, data } = item

    switch (operation) {
      case 'create': {
        // Node numbers are server-assigned and unique per job (REL-03);
        // retry once if a concurrent client claims the number first
        const insertNode = async () => {
          const { data: nodeNumber, error: numberError } = await withTimeout(
            supabase.rpc('assign_node_number', { p_job_id: data.job_id }),
            SYNC_OP_TIMEOUT_MS,
            'node number'
          )
          if (numberError) return { data: null, error: numberError }
          return withTimeout(
            supabase
              .from('inspection_nodes')
              .insert({ ...data, node_number: nodeNumber })
              .select()
              .single(),
            SYNC_OP_TIMEOUT_MS,
            'node insert'
          )
        }

        let { data: serverNode, error: createError } = await insertNode()
        if (createError?.code === '23505') {
          if (data.id && isPrimaryKeyConflict(createError)) {
            // Replay: the interactive attempt (or an earlier queue run)
            // already created this node but its response was lost — fetch
            // the existing row instead of duplicating it
            ;({ data: serverNode, error: createError } = await withTimeout(
              supabase.from('inspection_nodes').select('*').eq('id', data.id).single(),
              SYNC_OP_TIMEOUT_MS,
              'node refetch'
            ))
          } else {
            ;({ data: serverNode, error: createError } = await insertNode())
          }
        }
        if (createError) throw createError
        if (!serverNode) throw new Error('Node insert returned no row')

        if (entityId.startsWith('temp_')) {
          // Legacy offline node (pre client-generated ids): swap in the
          // server-assigned id everywhere it is still referenced — the local
          // node/photo stores and queued items (photos captured against this
          // node, later updates/deletes) — so they sync against the real
          // row. processQueue re-reads items before processing, so photos
          // queued behind this node in the same run pick up the remap.
          await offlineStorageService.remapNodeId(entityId, serverNode)
        } else {
          // Same id everywhere already — just refresh the local record with
          // the authoritative server row (node_number is server-assigned)
          await offlineStorageService.saveNode(serverNode)
        }
        break
      }

      case 'update': {
        const { error: updateError } = await supabase
          .from('inspection_nodes')
          .update(data)
          .eq('id', entityId)
        if (updateError) throw updateError
        break
      }

      case 'delete': {
        // The node's photo rows cascade away with it, taking their storage
        // keys — collect the files first
        await purgeNodeStorage(entityId)
        const { error: deleteError } = await supabase
          .from('inspection_nodes')
          .delete()
          .eq('id', entityId)
        if (deleteError) throw deleteError
        break
      }
    }
  },

  /**
   * Sync photo operations
   */
  async syncPhoto(item: SyncQueueItem): Promise<void> {
    const { operation, entityId, data } = item

    switch (operation) {
      case 'create': {
        // Photos captured in a job that was itself created offline carry a
        // temp_ job id — and a temp-prefixed storage key. The queue is
        // timestamp-ordered so the job's create normally runs (and remaps)
        // first; resolve through the durable alias when it already has,
        // otherwise wait with a clear message.
        let photoData = data
        if (
          typeof photoData.job_id === 'string' &&
          (photoData.job_id as string).startsWith('temp_')
        ) {
          const jobAlias = await offlineStorageService.getIdAlias(photoData.job_id)
          if (!jobAlias) {
            throw new Error('Photo is waiting for its job to sync first')
          }
          const prefix = `${photoData.job_id}/`
          const remappedPath =
            typeof photoData.storage_path === 'string' &&
            (photoData.storage_path as string).startsWith(prefix)
              ? `${jobAlias.server_id}/${(photoData.storage_path as string).slice(prefix.length)}`
              : photoData.storage_path
          photoData = { ...photoData, job_id: jobAlias.server_id, storage_path: remappedPath }
          item.data = photoData
          await offlineStorageService.putQueueItem(item)
        }

        // Same story for a temp_ node id (the insert would violate the FK):
        // check the durable alias first — the node may have synced and been
        // remapped while this item was queued — then fall back to
        // coordinate-matching the lingering temp record (pre-remap builds
        // discarded the server id). If the node simply hasn't synced yet
        // (or the match is ambiguous), fail with a clear message so the
        // dead-letter panel explains itself.
        if (
          typeof photoData.node_id === 'string' &&
          (photoData.node_id as string).startsWith('temp_')
        ) {
          const nodeAlias = await offlineStorageService.getIdAlias(photoData.node_id)
          let serverNodeId: string | null = nodeAlias?.server_id ?? null
          if (!serverNodeId) {
            const match = await offlineStorageService.findSyncedNodeMatch(photoData.node_id)
            if (match) {
              await offlineStorageService.remapNodeId(photoData.node_id, match)
              serverNodeId = match.id
            }
          }
          if (!serverNodeId) {
            throw new Error('Photo is waiting for its tree to sync first')
          }
          photoData = { ...photoData, node_id: serverNodeId }
          item.data = photoData
          await offlineStorageService.putQueueItem(item)
        }

        // Recover unusable persisted keys: legacy items stored an ephemeral
        // blob: URL as storage_path, and pre-sanitising captures embedded
        // raw file names whose characters storage rejects (spaces). Such a
        // key can never have been accepted server-side, so regenerating is
        // safe; it is persisted back onto the item so retries reuse it.
        let storagePath: string = photoData.storage_path
        if (typeof storagePath !== 'string' || !isValidStorageKey(storagePath)) {
          const fileName = sanitizeStorageFileName(
            typeof photoData.file_name === 'string' ? photoData.file_name : 'photo.jpg'
          )
          storagePath = `${photoData.job_id}/${uuidv4()}_${fileName}`
          item.data = { ...photoData, storage_path: storagePath }
          await offlineStorageService.putQueueItem(item)
        }

        // Upload bytes come from the PHOTO_BLOBS store first, falling back
        // to the File embedded in the queue item. Either way, materialise
        // them into an ArrayBuffer before upload: iOS Safari can serialise
        // a File restored from IndexedDB into an EMPTY request body, which
        // storage rejects with "No content provided" — while the same bytes
        // read fine via arrayBuffer().
        const storedBlob = await offlineStorageService.getPhotoBlob(entityId)
        const uploadSource: Blob | undefined = storedBlob ?? photoData.file ?? undefined
        if (uploadSource) {
          const uploadBody =
            typeof uploadSource.arrayBuffer === 'function'
              ? await uploadSource.arrayBuffer()
              : uploadSource
          if (uploadBody instanceof ArrayBuffer && uploadBody.byteLength === 0) {
            throw new Error('Local copy of the photo has no bytes — cannot upload')
          }
          const { error: uploadError } = await withTimeout(
            supabase.storage.from('photos').upload(storagePath, uploadBody, {
              contentType: photoData.mime_type || undefined,
              upsert: false,
            }),
            SYNC_OP_TIMEOUT_MS,
            'photo upload'
          )
          // A duplicate upload means a previous attempt got the file up
          // before failing later; treat it as success so retries converge
          if (uploadError && !isDuplicateUploadError(uploadError)) throw uploadError
        }

        // Replay guard: an earlier attempt (interactive capture or a prior
        // queue run) may have inserted the row before its response was lost.
        // The storage key is unique per capture, so it identifies the row.
        const { data: existingPhoto, error: checkError } = await withTimeout(
          supabase.from('photos').select('*').eq('storage_path', storagePath).maybeSingle(),
          SYNC_OP_TIMEOUT_MS,
          'photo replay check'
        )
        if (checkError) throw checkError

        // The queue item carries the full local photo row plus the raw file.
        // A client-generated id rides along so a replayed insert collides on
        // the primary key instead of duplicating; legacy temp_ ids are
        // stripped for the server to assign.
        const { file, ...photoRow } = photoData
        if (typeof photoRow.id === 'string' && (photoRow.id as string).startsWith('temp_')) {
          delete photoRow.id
        }
        const insertPhoto = () =>
          withTimeout(
            supabase
              .from('photos')
              .insert({ ...photoRow, storage_path: storagePath })
              .select()
              .single(),
            SYNC_OP_TIMEOUT_MS,
            'photo insert'
          )

        let serverPhoto = existingPhoto
        if (!serverPhoto) {
          let { data: insertedPhoto, error: createError } = await insertPhoto()
          if (createError?.code === '23505' && photoRow.id && isPrimaryKeyConflict(createError)) {
            // Replay of an insert whose response was lost — fetch the row
            ;({ data: insertedPhoto, error: createError } = await withTimeout(
              supabase.from('photos').select('*').eq('id', photoRow.id).single(),
              SYNC_OP_TIMEOUT_MS,
              'photo refetch'
            ))
          }
          if (createError) throw createError
          serverPhoto = insertedPhoto
        }
        if (!serverPhoto) throw new Error('Photo insert returned no row')

        if (entityId.startsWith('temp_')) {
          // Legacy offline capture: swap in the server id everywhere it is
          // still referenced — the local row, its stored bytes, and queued
          // caption edits or deletes keyed to the temp id
          await offlineStorageService.remapPhotoId(entityId, serverPhoto)
        } else {
          // Same id everywhere already — refresh the local copy with the
          // authoritative server row, keeping the stored bytes reachable
          await offlineStorageService.savePhoto(serverPhoto, storedBlob ?? file ?? undefined)
        }
        break
      }

      case 'delete': {
        // A delete still keyed to a temp_ id means the photo's create never
        // reached the server (a synced create would have remapped this
        // item): there is no server row — just drop any local leftovers.
        if (entityId.startsWith('temp_')) {
          await offlineStorageService.deletePhoto(entityId)
          break
        }

        // Look up the storage key; the row may already be gone (a replay of
        // a delete whose response was lost), which counts as success
        const { data: photoData, error: lookupError } = await withTimeout(
          supabase
            .from('photos')
            .select('storage_path, thumbnail_path')
            .eq('id', entityId)
            .maybeSingle(),
          SYNC_OP_TIMEOUT_MS,
          'photo delete lookup'
        )
        if (lookupError) throw lookupError

        if (photoData) {
          await withTimeout(
            purgePhotoStorage(photoData),
            SYNC_OP_TIMEOUT_MS,
            'photo storage delete'
          )
        }

        const { error: deleteError } = await withTimeout(
          supabase.from('photos').delete().eq('id', entityId),
          SYNC_OP_TIMEOUT_MS,
          'photo delete'
        )
        if (deleteError) throw deleteError

        // An in-flight create that raced this delete may have re-saved the
        // local copy after the interactive delete removed it
        await offlineStorageService.deletePhoto(entityId)
        break
      }

      case 'update': {
        const { error: updateError } = await withTimeout(
          supabase.from('photos').update(data).eq('id', entityId),
          SYNC_OP_TIMEOUT_MS,
          'photo update'
        )
        if (updateError) throw updateError
        break
      }
    }
  },

  /**
   * Sync report operations
   */
  async syncReport(item: SyncQueueItem): Promise<void> {
    const { operation, entityId, data } = item

    switch (operation) {
      case 'create': {
        const { error: createError } = await supabase.from('reports').insert(data)
        if (createError) throw createError
        break
      }

      case 'update': {
        const { error: updateError } = await supabase
          .from('reports')
          .update(data)
          .eq('id', entityId)
        if (updateError) throw updateError
        break
      }

      case 'delete': {
        const { error: deleteError } = await supabase.from('reports').delete().eq('id', entityId)
        if (deleteError) throw deleteError
        break
      }
    }
  },

  /**
   * Get queue statistics
   */
  async getQueueStats(): Promise<{
    pending: number
    inProgress: number
    failed: number
  }> {
    const allItems = await offlineStorageService.getQueueItems()
    return {
      pending: allItems.filter((item) => item.status === 'pending').length,
      inProgress: allItems.filter((item) => item.status === 'in_progress').length,
      failed: allItems.filter((item) => item.status === 'failed').length,
    }
  },

  /**
   * Get dead-lettered items awaiting manual retry/discard
   */
  async getFailedItems(): Promise<SyncQueueItem[]> {
    const allItems = await offlineStorageService.getQueueItems()
    return allItems
      .filter((item) => item.status === 'failed')
      .sort((a, b) => a.timestamp - b.timestamp)
  },

  /**
   * Manually retry a dead-lettered item: reset its retry budget and process
   * it immediately. On failure it re-enters the automatic retry cycle.
   */
  async retryFailedItem(id: string): Promise<void> {
    const allItems = await offlineStorageService.getQueueItems()
    const item = allItems.find((i) => i.id === id)
    if (!item || item.status !== 'failed') return

    item.retryCount = 0
    item.error = undefined
    item.status = 'pending'
    await offlineStorageService.putQueueItem(item)

    await this.processSyncItem(item)
  },

  /**
   * Permanently discard a single dead-lettered item
   */
  async discardFailedItem(id: string): Promise<void> {
    await offlineStorageService.deleteQueueItem(id)
  },

  /**
   * Clear failed items from queue
   */
  async clearFailedItems(): Promise<void> {
    const allItems = await offlineStorageService.getQueueItems()
    const failedItems = allItems.filter((item) => item.status === 'failed')

    for (const item of failedItems) {
      await offlineStorageService.deleteQueueItem(item.id)
    }
  },
}
